import React, { useEffect, useState, useMemo } from 'react';
import Cookies from 'js-cookie';
import Head from 'next/head';
import Router from 'next/router';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { getTheme } from '../constants/theme';
import { ContextState } from '/context';
import MainLayout from '/layouts/MainLayout';
import '../styles/globals.css';
import '../styles/tailwind.css';
import '../styles/admin-theme.css';
import '../utils/fontawesome';
import i18n from 'i18next';
import '/i18n';
import { appWithTranslation } from 'react-i18next';
import { CHANGE_LANG } from 'context/actions/auth';

import ProgressBar from '@badrap/bar-of-progress';

const progress = new ProgressBar({
    size: 3,
    color: '#2F4858',
    className: 'bar-of-progress',
    delay: 100,
});

Router.events.on('routeChangeStart', progress.start);
Router.events.on('routeChangeComplete', progress.finish);
Router.events.on('routeChangeError', progress.finish);

function MyApp(props) {
    const { Component, pageProps } = props;

    const Layout = Component.Layout || MainLayout;

    const [mode, setMode] = useState('light');
    const appTheme = useMemo(() => getTheme(mode), [mode]);

    useEffect(() => {
        const jssStyles = document.querySelector('#jss-server-side');
        if (jssStyles) {
            jssStyles.parentElement.removeChild(jssStyles);
        }

        if (typeof window !== 'undefined') {
            const chosenLang = Cookies.get('chosen_lang');
            i18n.changeLanguage(chosenLang ? chosenLang : 'mn');

            const saved = localStorage.getItem('admin-theme') || 'light';
            setMode(saved);
            document.documentElement.setAttribute('data-admin-theme', saved);
        }

        const handler = () => {
            setMode((prev) => {
                const next = prev === 'dark' ? 'light' : 'dark';
                localStorage.setItem('admin-theme', next);
                document.documentElement.setAttribute('data-admin-theme', next);
                return next;
            });
        };
        window.addEventListener('toggle-admin-theme', handler);
        return () => window.removeEventListener('toggle-admin-theme', handler);
    }, []);

    return (
        <React.Fragment>
            <Head>
                <title>Khuleg Cargo</title>
                <link rel="shortcut icon" href="/logo.jpg" />
                <meta charSet="utf-8" />
                <meta property="og:title" content="Khuleg Cargo" key="title" />
                <meta property="og:image" content="/Logoo.png" key="ogimage" />
            </Head>
            <ThemeProvider theme={appTheme}>
                <CssBaseline />
                <ContextState>
                    <Layout>
                        <Component {...pageProps} />
                    </Layout>
                </ContextState>
            </ThemeProvider>
        </React.Fragment>
    );
}

MyApp.propTypes = {
    Component: PropTypes.elementType.isRequired,
    pageProps: PropTypes.object.isRequired,
};

export default MyApp;
